/**
 * dashboardRange - the period a dashboard card covers, and the one control
 * that changes it.
 *
 * Firm Economics used to hard-code a trailing 30 days into a constant and
 * print it as a card SUBTITLE. Two problems, and the second is the interesting
 * one: a subtitle reads as a description of the card, not as a setting you can
 * change, so nobody ever thought to look for a filter - and there wasn't one.
 *
 * So the window is now a VALUE, shown as a `Badge` beside the title (a badge
 * reads as state) and edited through `RangeControl`, which offers the four
 * periods people actually ask for, a specific month, and a free from/to pair.
 *
 * The presets are laid out TWO PER ROW on purpose. These cards are half a row
 * wide on a normal dashboard, and a single column of six buttons is a popover
 * taller than the card it hangs off.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { PiCalendarBlankLight, PiCaretDownLight, PiCheckLight } from 'react-icons/pi';
import Badge from '../../shared/TailwindComponents/Badge';
import Button from '../../shared/TailwindComponents/Button';
import { getIntlLocale } from '../../../utils/functions';

/** A resolved period: what the request is actually filtered by. */
export interface DashboardRange {
  /** `30d` | `mtd` | `90d` | `ytd` | `month:YYYY-MM` | `custom`. */
  key: string;
  /** Inclusive ISO date (yyyy-mm-dd). */
  from: string;
  /** Inclusive ISO date. Absent means "up to now". */
  to?: string;
}

export const iso = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const daysAgo = (days: number): Date => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date;
};

/** The trailing window every card ships on. */
export const DEFAULT_RANGE_KEY = '30d';

/** Resolve a preset id into real dates. Unknown ids fall back to `30d`. */
export const resolveRange = (key: string): DashboardRange => {
  const now = new Date();

  if (key === 'mtd') {
    return { key, from: iso(new Date(now.getFullYear(), now.getMonth(), 1)) };
  }
  if (key === '90d') return { key, from: iso(daysAgo(90)) };
  if (key === 'ytd') return { key, from: iso(new Date(now.getFullYear(), 0, 1)) };

  const month = /^month:(\d{4})-(\d{2})$/.exec(key);
  if (month) {
    const year = Number(month[1]);
    const index = Number(month[2]) - 1;
    return {
      key,
      from: iso(new Date(year, index, 1)),
      // Day 0 of the next month is the last day of this one.
      to: iso(new Date(year, index + 1, 0)),
    };
  }

  return { key: DEFAULT_RANGE_KEY, from: iso(daysAgo(30)) };
};

const monthLabel = (year: number, index: number, locale: string): string =>
  new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' })
    .format(new Date(year, index, 1));

const shortDate = (value: string, locale: string): string => {
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(parsed);
};

/**
 * What the badge says. Already translated, and short enough to sit in a header
 * next to a card title without wrapping it.
 */
export const rangeLabel = (
  range: DashboardRange,
  t: (key: string, options?: any) => any
): string => {
  const locale = getIntlLocale();
  if (range.key === 'mtd') return String(t('This month'));
  if (range.key === '90d') return String(t('Last 90 days'));
  if (range.key === 'ytd') return String(t('This year'));
  if (range.key.startsWith('month:')) {
    const [year, month] = range.key.slice(6).split('-');
    return monthLabel(Number(year), Number(month) - 1, locale);
  }
  if (range.key === 'custom') {
    return range.to
      ? `${shortDate(range.from, locale)} - ${shortDate(range.to, locale)}`
      : `${shortDate(range.from, locale)} –`;
  }
  return String(t('Last {{count}} days', { count: 30 }));
};

/** The last `count` months, newest first - the month picker's options. */
const recentMonths = (count: number): Array<{ key: string; label: string }> => {
  const locale = getIntlLocale();
  const now = new Date();
  return Array.from({ length: count }, (_, offset) => {
    const date = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    const key = `month:${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    return { key, label: monthLabel(date.getFullYear(), date.getMonth(), locale) };
  });
};

interface RangeControlProps {
  value: DashboardRange;
  onChange: (range: DashboardRange) => void;
  /** For the popover's test id and its accessible name. */
  label: string;
  'data-testid'?: string;
}

const PRESETS = [
  { key: '30d', label: 'Last 30 days' },
  { key: 'mtd', label: 'This month' },
  { key: '90d', label: 'Last 90 days' },
  { key: 'ytd', label: 'This year' },
];

/**
 * The period picker: presets two-up, a month list, and a free from/to pair.
 *
 * A popover rather than a `Dropdown` because "custom" needs two date fields
 * inside it, and a select cannot hold a form.
 */
export const RangeControl: React.FC<RangeControlProps> = ({
  value,
  onChange,
  label,
  'data-testid': testId = 'dashboard-range',
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(value.from);
  const [to, setTo] = useState(value.to || iso(new Date()));
  const wrapper = useRef<HTMLDivElement>(null);

  // A popover that outlives a click elsewhere on the page is a popover that
  // covers whatever the user was reaching for.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event: MouseEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const months = useMemo(() => recentMonths(6), []);

  const pick = useCallback(
    (key: string) => {
      onChange(resolveRange(key));
      setOpen(false);
    },
    [onChange]
  );

  const applyCustom = useCallback(() => {
    if (!from) return;
    onChange({ key: 'custom', from, to: to || undefined });
    setOpen(false);
  }, [from, to, onChange]);

  return (
    <div className="relative" ref={wrapper}>
      <button
        type="button"
        data-testid={testId}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((current) => !current)}
        className="flex items-center gap-1 rounded-md px-1 py-0.5 transition-colors duration-200 ease-slide hover:bg-surface-sunken/70 focus:outline-none focus:ring-1 focus:ring-champagne/60"
      >
        <Badge color="gray" icon={<PiCalendarBlankLight className="h-3 w-3" />}>
          {rangeLabel(value, t)}
        </Badge>
        <PiCaretDownLight aria-hidden className="h-3 w-3 text-fg-muted" />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={label}
                    className="glass dash-popover absolute end-0 top-full z-40 mt-2 w-[19rem] rounded-lg p-3 shadow-sm"
        >
          {/* Two per row: these cards are half-width, and a single column of
              presets makes a popover taller than the card it hangs off. */}
          <div className="grid grid-cols-2 gap-1.5">
            {PRESETS.map((preset) => {
              const active = value.key === preset.key;
              return (
                <button
                  key={preset.key}
                  type="button"
                  data-testid={`${testId}-preset-${preset.key}`}
                  aria-pressed={active}
                  onClick={() => pick(preset.key)}
                  className={clsx(
                    'flex items-center justify-between gap-1 rounded-md border px-2 py-1.5 text-start text-xs transition-colors duration-200 ease-slide focus:outline-none focus:ring-1 focus:ring-champagne/60',
                    active
                      ? 'border-champagne/60 bg-champagne/15 text-fg-strong'
                      : 'border-hairline-soft bg-surface-sunken/60 text-fg-body hover:border-champagne/40'
                  )}
                >
                  {t(preset.label)}
                  {active && <PiCheckLight aria-hidden className="h-3 w-3 text-champagne" />}
                </button>
              );
            })}
          </div>

          <p className="mt-3 text-[11px] font-medium text-fg-muted">
            {t('Month')}
          </p>
          <div className="mt-1.5 grid grid-cols-2 gap-1.5">
            {months.map((month) => {
              const active = value.key === month.key;
              return (
                <button
                  key={month.key}
                  type="button"
                  data-testid={`${testId}-${month.key}`}
                  aria-pressed={active}
                  onClick={() => pick(month.key)}
                  className={clsx(
                    'truncate rounded-md border px-2 py-1.5 text-start text-xs transition-colors duration-200 ease-slide focus:outline-none focus:ring-1 focus:ring-champagne/60',
                    active
                      ? 'border-champagne/60 bg-champagne/15 text-fg-strong'
                      : 'border-hairline-soft bg-surface-sunken/60 text-fg-body hover:border-champagne/40'
                  )}
                >
                  {month.label}
                </button>
              );
            })}
          </div>

          <p className="mt-3 text-[11px] font-medium text-fg-muted">
            {t('Custom range')}
          </p>
          <div className="mt-1.5 grid grid-cols-2 gap-1.5">
            <label className="block">
              <span className="sr-only">{t('From')}</span>
              <input
                type="date"
                value={from}
                max={to || undefined}
                data-testid={`${testId}-from`}
                onChange={(event) => setFrom(event.target.value)}
                className="w-full rounded-md border border-hairline-soft bg-surface-sunken/60 px-2 py-1.5 text-xs text-fg-body focus:outline-none focus:ring-1 focus:ring-champagne/60"
              />
            </label>
            <label className="block">
              <span className="sr-only">{t('To')}</span>
              <input
                type="date"
                value={to}
                min={from || undefined}
                data-testid={`${testId}-to`}
                onChange={(event) => setTo(event.target.value)}
                className="w-full rounded-md border border-hairline-soft bg-surface-sunken/60 px-2 py-1.5 text-xs text-fg-body focus:outline-none focus:ring-1 focus:ring-champagne/60"
              />
            </label>
          </div>
          <div className="mt-2 flex justify-end">
            <Button
              variant="outline"
              size="small"
              onClick={applyCustom}
              disabled={!from}
              data-testid={`${testId}-apply`}
            >
              {t('Apply')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RangeControl;
